'use strict'
import permissionService from '@/api/permissionService'
import { encrypt, decrypt } from '@/utils/crypto'

const PERMISSIONS_STORAGE_KEY = 'user_permissions'
const PERMISSIONS_TIMESTAMP_KEY = 'permissions_timestamp'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export const routePermissions = {
  // User Management
  '/users': 'view users',
  
  // Organization Management
  '/organizations': 'view organizations',
  '/organization': 'view organizations',
  
  // Store Management
  '/stores': 'view stores',
  '/store/:id': 'view stores',
  
  // Product Management
  '/products': 'view products',
  '/add-product': 'create products',
  '/product/:id': 'view products',
  '/edit-product/:id': 'edit products',
  
  // Category Management
  '/categories': 'view categories',
  '/category/:id/products': 'view categories',

  // Brand Management
  '/brands': 'view brands',

  // Online Storefront
  '/storefront': 'view storefront',
  
  // Unit Management
  '/units': 'view units',
  
  // Supplier Management
  '/suppliers': 'view suppliers',
  '/add-supplier': 'create suppliers',
  '/supplier/:id': 'view suppliers',
  '/edit-supplier/:id': 'edit suppliers',
  '/supplier/:id/products': 'view suppliers',
  
  // Customer Management
  '/customers': 'view customers',
  '/add-customer': 'create customers',
  '/customer/:id': 'view customers',
  '/edit-customer/:id': 'edit customers',
  
  // Purchase Management
  '/purchases': 'view purchases',
  '/purchase/:id': 'view purchases',
  
  // Direct Purchase Management
  '/direct-purchases': 'view direct purchases',
  '/direct-purchase/:id': 'view direct purchases',
  
  // Sales Management
  '/sales': 'view sales',
  '/store-sales': 'view sales',
  '/sale/:id': 'view sales',
  
  // Sale Return Management
  '/returns': 'view sale returns',
  '/returns/:id': 'view sale returns',
  
  // Invoice Management
  '/invoices': 'view invoices',
  '/invoice/:id': 'view invoices',
  
  // Quotation Management
  '/quotations': 'view quotations',
  '/quotation/:id': 'view quotations',
  
  // Stock Management
  '/store-products': 'view stock',
  '/stock-take': 'view stock takes',
  '/stock-take/:id': 'view stock takes',
  
  // Transfer Management
  '/transfers': 'view transfers',
  '/transfer/:id': 'view transfers',
  
  // Store Transfer Management
  '/store-transfers': 'view store transfers',
  '/store-transfer/:id': 'view store transfers',
  
  // Supplier Return Management
  '/supplier-returns': 'view supplier returns',
  '/supplier-return/:id': 'view supplier returns',
  
  // Supplier Payment Management
  '/supplier-payments': 'view supplier payments',
  
  // Order Management
  '/orders': 'view orders',
  '/order/:id': 'view orders',
  
  // Store Order Management
  '/all-store-orders': 'view store orders',
  '/store-orders': 'view store orders',
  '/store-order/:id': 'view store orders',
  
  // Shift Management
  '/shifts': 'view shifts',
  
  // Z-Report Management
  '/z-reports': 'view z-reports',
  '/z-report/:id': 'view z-reports',
  
  // Analytics
  '/annual-report': 'view analytics',
  '/daily-report': 'view analytics',
  '/general-report': 'view analytics',
  '/stock-take-analytics': 'view stock take analytics',
  '/product-sales-with-stock': 'view product analytics',
  
  // Transaction Management
  '/transactions': 'view transactions',
  
  // Settings Management
  '/settings': 'view settings',
  '/logs': 'view logs',
  
  // Payment Integration Management
  '/mpesa-integrations': 'view integrations',
}

export default {
  async getUserPermissions(userId, forceRefresh = false) {
    if (!forceRefresh) {
      const cached = this.getCachedPermissions()
      if (cached) {
        return cached
      }
    }

    try {
      const response = await permissionService.getUserPermissions(userId)
      if (response && response.all_permissions) {
        this.cachePermissions(response.all_permissions)
        return response.all_permissions
      }
      return []
    } catch (error) {
      console.error('Error fetching user permissions:', error)
      return []
    }
  },

  getCachedPermissions() {
    try {
      const timestamp = sessionStorage.getItem(PERMISSIONS_TIMESTAMP_KEY)
      if (!timestamp) return null

      const now = Date.now()
      if (now - parseInt(timestamp) > CACHE_DURATION) {
        this.clearPermissionsCache()
        return null
      }

      const encrypted = sessionStorage.getItem(PERMISSIONS_STORAGE_KEY)
      if (!encrypted) return null

      const decrypted = decrypt(encrypted)
      return JSON.parse(decrypted)
    } catch (error) {
      console.error('Error reading cached permissions:', error)
      return null
    }
  },

  cachePermissions(permissions) {
    try {
      const encrypted = encrypt(JSON.stringify(permissions))
      sessionStorage.setItem(PERMISSIONS_STORAGE_KEY, encrypted)
      sessionStorage.setItem(PERMISSIONS_TIMESTAMP_KEY, Date.now().toString())
    } catch (error) {
      console.error('Error caching permissions:', error)
    }
  },

  clearPermissionsCache() {
    sessionStorage.removeItem(PERMISSIONS_STORAGE_KEY)
    sessionStorage.removeItem(PERMISSIONS_TIMESTAMP_KEY)
  },

  hasPermission(userPermissions, requiredPermission) {
    if (!userPermissions || !Array.isArray(userPermissions)) {
      return false
    }
    return userPermissions.includes(requiredPermission)
  },

  getRequiredPermission(path) {
    for (const [route, permission] of Object.entries(routePermissions)) {
      if (this.matchRoute(path, route)) {
        return permission
      }
    }
    return null
  },

  matchRoute(path, pattern) {
    if (path === pattern) return true

    const pathParts = path.split('/').filter(Boolean)
    const patternParts = pattern.split('/').filter(Boolean)

    if (pathParts.length !== patternParts.length) return false

    return patternParts.every((part, index) => {
      if (part.startsWith(':')) return true
      return part === pathParts[index]
    })
  },

  async checkRoutePermission(to, profile) {
    const requiredPermission = this.getRequiredPermission(to.path)
    
    if (!requiredPermission) {
      return true
    }

    const userPermissions = await this.getUserPermissions(profile.id)
    
    if (profile.role === 'super-admin' || profile.role === 'primary') {
      return true
    }

    return this.hasPermission(userPermissions, requiredPermission)
  }
}
